import { cn } from "@/lib/utils";

import { PreviewCanvasScale } from "./shared/preview-canvas-scale";

const INTER_TABULAR = {
  fontFeatureSettings: '"ss03" 1, "ss01" 1, "zero" 1, "ss02" 1',
} as const;

const INSET_SHADOW = "inset 0.86px 1.721px 10px 0px rgba(0, 0, 0, 0.1)";
const INSET_SHADOW_BLUE =
  "inset 0.86px 1.721px 10px 0px rgba(255, 255, 255, 0.25)";

/** Figma 11115:59248–59254 — stacked segments on 375×14.176 track. */
const PROGRESS_SEGMENTS = [
  {
    left: 0,
    top: 0,
    width: 375,
    height: 14.176,
    fill: "#FFFFFF",
    shadow: INSET_SHADOW,
  },
  {
    left: 0.31,
    top: 0.12,
    width: 156,
    height: 14,
    fill: "#F9F9F8",
    shadow: INSET_SHADOW,
  },
  {
    left: 0,
    top: 0,
    width: 300,
    height: 14.176,
    fill: "#FFFFFF",
    shadow: INSET_SHADOW,
  },
  {
    left: 0.31,
    top: 0.12,
    width: 240,
    height: 14,
    fill: "#F9F9F8",
    shadow: INSET_SHADOW,
  },
  {
    left: 0.31,
    top: 0.12,
    width: 96,
    height: 14,
    fill: "#E5E6E6",
    shadow: INSET_SHADOW,
  },
  {
    left: 0.31,
    top: 0.12,
    width: 58,
    height: 14,
    fill: "#001EFF",
    shadow: INSET_SHADOW_BLUE,
  },
] as const;

/** Figma 11115:59255 — absolute marker positions within 379.172×21 container. */
const TIME_MARKERS = [
  {
    label: "1 Week",
    left: 0,
    top: 1.5,
    className: "text-[12px] tracking-[-0.24px]",
  },
  {
    label: "1 Month",
    left: 90,
    top: 1.5,
    className: "text-[12px] tracking-[-0.24px]",
  },
  {
    label: "2 Months",
    left: 184,
    top: 1.5,
    className: "text-[12px] tracking-[-0.24px]",
  },
  {
    label: "15",
    left: 287,
    top: 0,
    className: "text-[14px] tracking-[-0.28px]",
  },
  {
    label: "20",
    left: 352,
    top: 0,
    className: "text-[14px] tracking-[-0.28px]",
  },
  {
    label: "25",
    left: 421,
    top: 0,
    className: "text-[14px] tracking-[-0.28px]",
  },
] as const;

function ProgressSegment({
  left,
  top,
  width,
  height,
  fill,
  shadow,
}: (typeof PROGRESS_SEGMENTS)[number]) {
  return (
    <div
      aria-hidden
      className="absolute overflow-hidden rounded-[43.016px]"
      style={{ left, top, width, height }}
    >
      <div
        className="absolute inset-0 rounded-[43.016px]"
        style={{ backgroundColor: fill, boxShadow: shadow }}
      />
    </div>
  );
}

/**
 * Backward planner inset (Figma 11115:59240, 391×291).
 * Scales down on the mobile preview shell (243×181) and renders 1:1 from md+.
 */
export function BackwardPlannerPreview() {
  return (
    <PreviewCanvasScale testId="backward-planner-canvas">
      <p
        className="absolute top-[23px] left-[24px] text-[12px] leading-[1.4] font-normal whitespace-nowrap text-[#0d0d0d]"
        style={INTER_TABULAR}
      >
        Target Black Friday date
      </p>

      <div className="absolute top-[52px] left-[24px] h-[108px] w-[441px] overflow-hidden rounded-[10px] border border-[#e5e6e6] bg-white">
        <div className="absolute top-[15.881px] left-[15.691px] h-[80.176px] w-[379.172px]">
          <p
            className="absolute top-0 left-0 w-full text-[14px] leading-[1.5] font-normal tracking-[-0.28px] text-[#b0b3b5]"
            style={INTER_TABULAR}
          >
            Inventory lead time
          </p>

          <div className="absolute top-[37px] left-0 h-[43.176px] w-[379.172px]">
            <div
              aria-hidden
              className="absolute top-0 left-0 h-[14.176px] w-[375px]"
            >
              {PROGRESS_SEGMENTS.map((segment, index) => (
                <ProgressSegment key={index} {...segment} />
              ))}
            </div>

            <div className="absolute top-[22.176px] left-0 h-[21px] w-[379.172px] leading-[1.5] whitespace-nowrap text-[#b0b3b5]">
              {TIME_MARKERS.map((marker) => (
                <span
                  key={marker.label}
                  className={cn("absolute font-normal", marker.className)}
                  style={{
                    ...INTER_TABULAR,
                    left: marker.left,
                    top: marker.top,
                  }}
                >
                  {marker.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PreviewCanvasScale>
  );
}
